import React, { useEffect, useMemo, useState } from "react";
import { Card, Col, Row, Image as AntdImage, Space } from "antd";
import Layout from "@/HOC";
import styles from "./collection-view.module.css";
import { SVG } from "@/assets/svg";
import Link from "next/link";
// import { getSingleCatalog } from "@/utils/apis/collection/get-single-catalog.api";
import { catalogPublicSlug, storedSlugCandidates } from "@/utils/catalogSlug";
import { getAllCatalogs } from "@/utils/apis/collection/get-all-catalogs.api";
import { GetServerSideProps, NextPage } from "next";
import { ICatalog } from "@/interfaces/collection.interface";
import { formatDate, formatDateInSlash, navigateToLink } from "@/utils/common";
import PopupModal from "@/component/modals/PopupModal";
import SocialShare from "@/component/social-link";
import ListView from "@/component/list";
import { $public } from "@/utils/client";
import { useAppContext } from "@/context";
// import { likeCatalog } from "@/utils/apis/collection/like-catalog.api";
import { errorToast, successToast } from "@/component/toasts";
import DefaultLoader from "@/component/loader";
import { Colors } from "@/styles/Colors";
import Image from "next/image";
import { IBackendImage } from "@/interfaces/common.interface";
import { likeCatalog } from "@/utils/apis/collection/like-catalog.api";
import { getSingleCatalog } from "@/utils/apis/collection/get-single-catalog.api";
import { IMAGES } from "@/assets/images";
import { postReport } from "@/utils/apis/minerals/like-share-comment.api";
import { useRouter } from "next/router";
import { getUserBadgeById } from "@/utils/apis/badges/badges.api"; // get both user and catalog badges
import {
  followUnfollowService,
  getFollowerById,
} from "@/utils/apis/minerals/explore-detail.api";
import {
  RotateLeftOutlined,
  RotateRightOutlined,
  ZoomInOutlined,
  ZoomOutOutlined,
} from "@ant-design/icons";
import MineralCard from "@/component/card/MineralCard";
import ButtonLoader from "@/component/buttonLoader/ButtonLoader";
import Head from "next/head";
import APIConstants from "@/constants/api.constants";
import { getCollectionSeo } from "@/utils/apis/collection/collection-seo.api";
import ToolTip from "@/component/tool-tip";
import { DefaultCanonical, PageKeywords, metaDescription, metaTitle } from "@/component/seo/DefaultSeo";

interface CatalogViewPageProps {
  data: {
    success: boolean;
    catalogDetails: ICatalog;
    collectionSeo?: any;
  };
}

export const getServerSideProps: GetServerSideProps<
  CatalogViewPageProps
> = async (context) => {
  const { params } = context;
  const id = params?.slug as string | undefined;
  if (!id) return { notFound: true };

  let response: any = null;
  for (const candidate of storedSlugCandidates(id)) {
    try {
      const res = await getSingleCatalog(candidate);
      if (res.data?.success && res.data?.data) {
        response = res;
        break;
      }
    } catch (error) {
      // try the next stored form
    }
  }

  // The public URL uses the owner's full name ("patricia-regans-collection"),
  // which the API doesn't know; find the catalog by searching on the first
  // name and matching the full-name slug.
  if (!response) {
    try {
      const firstWord = id.split("-")[0];
      const listRes = await getAllCatalogs({ page: 1, limit: 100, search: firstWord });
      const match = (listRes.data?.data || []).find(
        (c: any) => catalogPublicSlug(c) === id,
      );
      if (match?.slug) {
        const res = await getSingleCatalog(match.slug);
        if (res.data?.success && res.data?.data) response = res;
      }
    } catch (error) {
      // fall through to notFound
    }
  }
  if (!response) return { notFound: true };

  // Old URLs ("annas-collection", "anna-s-collection") redirect to the
  // canonical full-name URL.
  const canonicalSlug = catalogPublicSlug(response.data.data);
  if (canonicalSlug && canonicalSlug !== id) {
    return {
      redirect: {
        destination: `/collection-view/${encodeURIComponent(canonicalSlug)}`,
        permanent: false,
      },
    };
  }

  let collectionSeo = null;
  if (response.data.success) {
    const userId = response.data.data?.postUserId?._id || id;
    try {
      const seoRes = await getCollectionSeo(userId as string);

      console.log("seoRes", seoRes);
      if (seoRes.data.success) {
        collectionSeo = seoRes.data.data;
      }
    } catch (error) {
      console.error("Error fetching collection SEO:", error);
    }
  }

  return {
    props: {
      data: {
        success: response.data.success,
        catalogDetails: {
          ...response.data.data,
          coverImage: response.data?.data?.coverImage?.path || "",
        },
        collectionSeo,
      },
    },
  };
};

const SingleCatalogView: NextPage<CatalogViewPageProps> = ({ data }) => {
  const { catalogDetails, success } = data;
  const { user, handleLoginPopupOpen } = useAppContext();
  const [commentLoading, setCommentLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [followButton, setFollowButton] = useState("");
  const [buttonLoading, setButtonLoading] = useState<boolean>(false);
  const [catalogBadge, setCatalogBadge] = useState<any[]>([]);
  const [catalogBadgeName, setCatalogBadgeName] = useState<string>("");
  const [userBadgeName, setUserBadgeName] = useState<string>("");
  const [userBadge, setUserBadge] = useState<any[]>([]);
  const router = useRouter();
  const { query } = router;

  const currentPageName = router.pathname.replace(/^\//, "").replace(/-/g, " ");
  const formattedPageName =
    currentPageName.charAt(0).toUpperCase() + currentPageName.slice(1);

  const getLikeStatus = () => {
    return user?._id && catalogDetails.likedBy.includes(user?._id);
  };
  const [isSocialPopupVisible, setSocialPopupVisible] = useState(false);
  const [liked, setLiked] = useState(getLikeStatus());

  const userBadges = async (haveToLoad?: boolean) => {
    !haveToLoad && setLoading(true);
    try {
      const Id: any = catalogDetails?.postUserId?._id;
      const res = await getUserBadgeById(Id);
      if (res?.data?.success && Array.isArray(res?.data?.data)) {
        setCatalogBadge(
          res?.data.data.some(
            (badge: any) =>
              badge.userId === Id && badge.badgeId?.type === "Catalog-Likes",
          ),
        );
        setUserBadge(
          res?.data.data.some(
            (badge: any) =>
              badge.userId === Id && badge.badgeId?.type === "Mineral-Count",
          ),
        );
        const catalogBadge = res.data.data.find(
          (badge: any) =>
            badge.userId === Id && badge.badgeId?.type === "Catalog-Likes",
        );
        setCatalogBadgeName(catalogBadge?.badgeId?.badgeName || "");

        // Find Mineral-Count badge and set its name
        const mineralBadge = res.data.data.find(
          (badge: any) =>
            badge.userId === Id && badge.badgeId?.type === "Mineral-Count",
        );

        setUserBadgeName(mineralBadge?.badgeId?.badgeName || "");
      } else {
        console.error("CatalogBadge Error: Unexpected response format", res);
      }
    } catch (error) {
      console.error("CatalogBadge API Error:", error);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    setLiked(getLikeStatus());
    userBadges();
    fetchFollwerById();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const userName = useMemo(() => {
    if (!catalogDetails.postUserId?.firstName) return "My";
    return `${catalogDetails.postUserId?.firstName} ${catalogDetails.postUserId?.lastName}`;
  }, [catalogDetails]);

  if (!success) {
    return null;
  }

  const handleLike = async () => {
    setLiked(!liked);

    const response = await likeCatalog(catalogDetails._id as string);
    console.log("<postLike>response", response);
    if (!response.data.success) {
      console.log("<postLike>error", response.data.message);
      errorToast(response.data.message);
    }
  };

  const followUnfollow = async (followingId: any) => {
    setButtonLoading(true);
    try {
      const userDetails = user?._id;
      const res: any = await followUnfollowService({
        followingId,
        userDetails,
      });
      const message = res?.data?.message;

      if (message === "Followed") {
        setFollowButton("Following");
        successToast("User followed successfully");
      } else if (message === "Unfollowed") {
        setFollowButton("Follow");
        successToast("User unfollowed successfully");
      } else {
        console.log("<followUnfollow> Unexpected message:", message);
      }
    } catch (error) {
      console.error("<followUnfollow> Error:", error);
    } finally {
      setButtonLoading(false);
    }
  };

  const handleReportUser = async () => {
    try {
      setCommentLoading(true);
      const response = await postReport({
        type: "user",
        reportedBy: user?._id,
        reportedUser: catalogDetails?.postUserId?._id,
      });
      if (response.data.success === false) {
        errorToast("You already reported this user");
        return;
      }
      successToast("User reported successfully");
      return response.data.data;
    } catch (error: any) {
      console.error("API Error:", error);
      if (error?.status === 400) {
        errorToast("You already reported this user");
      }
      // throw error;
    } finally {
      setCommentLoading(false);
    }
  };

  const fetchFollwerById = async () => {
    try {
      const id = user?._id;
      if (!id) return;
      const following = catalogDetails?.postUserId?._id;
      const res = await getFollowerById(id, { following });
      if (res?.data?.success) {
        setFollowButton("Following");
      } else if (res?.data?.success === false) {
        setFollowButton("Follow");
        console.log("<Mycollection> Error", res?.data?.success);
      }
    } catch (error) {
      console.log("<Myfollower> Error", error);
    }
  };
  const originalTitle = data.collectionSeo?.title ||
    catalogDetails?.title ||
    (userName ? `${userName}${userName === "My" ? "" : "'s"} Collection` : formattedPageName);
  const ogImagePath =
    data.catalogDetails?.otherImages?.[data.catalogDetails?.displayImageIndex]
      ?.image?.path;
  const ogImage = ogImagePath ? `${APIConstants.image_url}${ogImagePath}` : "";
  const truncatedTitle = originalTitle || "";
  const truncatedDescription = data.collectionSeo?.description || "";

  return (
    <div>
      <DefaultCanonical url={data.collectionSeo?.canonicalUrl} />
      <PageKeywords keywords={data.collectionSeo?.keywords} title={originalTitle} description={data.collectionSeo?.description || data.catalogDetails?.aboutUs || ""} />
      <Head>
        <title key="title">{metaTitle(truncatedTitle, data.collectionSeo?.title)}</title>
        <meta
          key="description"
          name="description"
          content={metaDescription(data.collectionSeo?.description || data.catalogDetails.aboutUs || "", data.collectionSeo?.description)}
        />
        <meta
          key="og:title"
          property="og:title"
          content={originalTitle}
        />

        <meta
          key="og:description"
          property="og:description"
          content={metaDescription(data.collectionSeo?.description || data.catalogDetails.aboutUs || "", data.collectionSeo?.description)}
        />
        <meta
          key="og:image"
          property="og:image"
          content={ogImage}
        />
        <meta key="og:type" property="og:type" content="website" />
        <meta key="twitter:card" name="twitter:card" content="summary_large_image" />
        <meta
          key="twitter:title"
          name="twitter:title"
          content={originalTitle}
        />
        <meta
          key="twitter:description"
          name="twitter:description"
          content={data.collectionSeo?.description || data.catalogDetails?.aboutUs || ""}
        />
        <meta
          key="twitter:image"
          name="twitter:image"
          content={ogImage}
        />
        <meta
          key="viewport"
          name="viewport"
          content="width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no"
        />
      </Head>
      <Layout>
        {/* <div className={`${styles.Navbar}`}>
        <div className={`${styles.header}`}>
          <Link href="/home">
            <SVG.Logo1
              style={{
                width: "230px",
                margin: "0 auto",
              }}
            />
          </Link>
        </div>
      </div> */}
        <div className={`${styles.banner}`}>
          <Image
            src={
              catalogDetails?.coverImage
                ? $public(catalogDetails?.coverImage as string)
                : IMAGES.CoverPlaceholder
            }
            alt=""
            width={0}
            height={0}
            sizes="100vw"
            style={{
              width: "100%",
              height: "inherit",
              aspectRatio: 32 / 9,
              objectFit: "cover",
            }}
          />
          {/* hide it until styling is not fixed properly */}
          {userBadge ? (
            <div
              className={`${styles.ribbonwrapper}`}
              style={{
                top: "80px",
                left: "-16px",
                textAlign: "end",
                position: "absolute",
              }}
            >
              <p className={`${styles.ribbon}`}>{userBadgeName}</p>
            </div>
          ) : null}
          {catalogBadge ? (
            <div
              className={`${styles.ribbonwrapper1}`}
              style={{
                top: "84px",
                position: "absolute",
                textAlign: "center",
              }}
            >
              <p className={`${styles.ribbon1}`}>{catalogBadgeName}</p>
            </div>
          ) : null}
        </div>

        <div className={`${styles.quartz}`}>
          <div className="main">
            <div className={`${styles.part}`}>
              <div className={`${styles.Part}`}>
                <div className={`${styles.logo}`}>
                  {/* <SVG.Logo2 width="50px" /> */}
                  {catalogDetails.icon ? (
                    <Image
                      src={$public((catalogDetails.icon as IBackendImage).path)}
                      alt={""}
                      width={0}
                      height={0}
                      sizes="100vw"
                      style={{
                        width: "100%",
                        height: "100%",
                        borderRadius: "50%",
                        objectFit: "cover",
                      }}
                    />
                  ) : (
                    <SVG.Logo2 width="50px" />
                  )}
                </div>
                <h6 className={`${styles.inputlayout}`}>
                  {catalogDetails?.title || userName + "'s Collection"}
                </h6>
              </div>
              <div>
                <div className={`${styles.UpdateMsg}`}>
                  <SVG.Calender width="20px" />
                  <h6 className={`${styles.Msg}`}>
                    Edited on:
                    <span>{formatDate(catalogDetails.updatedAt)}</span>{" "}
                  </h6>
                </div>
                <div className={`${styles.CountMsg}`}>
                  {user && followButton ? (
                    <button
                      onClick={() =>
                        followUnfollow(catalogDetails?.postUserId?._id)
                      }
                      className="heart"
                      disabled={buttonLoading}
                    >
                      {buttonLoading ? (
                        <ButtonLoader />
                      ) : followButton === "Follow" ? (
                        <>
                          <SVG.AddCircle width="20px" /> Follow
                        </>
                      ) : (
                        followButton
                      )}
                    </button>
                  ) : user ? (
                    <div
                      style={{
                        marginTop: "5px",
                        backgroundColor: "#00000000",
                      }}
                    >
                      <DefaultLoader
                        size="small"
                        spinSize={18}
                        color="#1677ff"
                      />
                    </div>
                  ) : null}

                  <button
                    onClick={user ? handleLike : handleLoginPopupOpen}
                    className="heart"
                  >
                    <SVG.Heart fill={liked ? Colors.primary : "transparent"} />
                    {liked ? "Liked" : "Like"}
                  </button>
                  <button
                    onClick={() => setSocialPopupVisible(true)}
                    className="heart"
                  >
                    <SVG.Send />
                    Share
                  </button>
                  {user?._id !== catalogDetails?.postUserId?._id ? (
                    <button
                      onClick={user ? handleReportUser : handleLoginPopupOpen}
                      className="heart"
                    >
                      <SVG.Flag />
                      Report
                    </button>
                  ) : null}
                </div>
                <div>
                  <ul className={`${styles.ours}`}>
                    {catalogDetails?.facebookLink && (
                      <li>
                        <a
                          href={"//" + catalogDetails?.facebookLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => {
                            e.preventDefault();
                            navigateToLink(catalogDetails?.facebookLink);
                          }}
                        >
                          <SVG.Facebook width="20px" />
                        </a>
                      </li>
                    )}
                    {catalogDetails?.twitterLink && (
                      <li>
                        <a
                          href={"//" + catalogDetails?.twitterLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => {
                            e.preventDefault();
                            navigateToLink(catalogDetails?.twitterLink);
                          }}
                        >
                          <SVG.Output width="20px" />
                        </a>
                      </li>
                    )}
                    {catalogDetails?.linkedInLink && (
                      <li>
                        <a
                          href={"//" + catalogDetails?.linkedInLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => {
                            e.preventDefault();
                            navigateToLink(catalogDetails?.linkedInLink);
                          }}
                        >
                          <SVG.Linkedin width="20px" />
                        </a>
                      </li>
                    )}
                    {catalogDetails?.instagramLink && (
                      <li>
                        <a
                          href={"//" + catalogDetails?.instagramLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => {
                            e.preventDefault();
                            navigateToLink(catalogDetails?.instagramLink);
                          }}
                        >
                          <SVG.Instagram width="20px" />
                        </a>
                      </li>
                    )}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="main">
          {catalogDetails?.aboutUs && (
            <Row>
              <h6 className={`${styles.otherImages}`}>About {userName}</h6>
              <div className={`${styles.otherContent}`}>
                {catalogDetails?.aboutUs}
              </div>
            </Row>
          )}
          {catalogDetails?.otherImages?.length > 0 && (
            <>
              <div className={`${styles.Upload}`}>
                <h6 className={`${styles.otherImages}`}>
                  {userName}
                  {userName !== "My" && "'s"} Images
                </h6>
              </div>
              <Row gutter={[16, 16]} style={{ marginTop: "1rem" }}>
                <AntdImage.PreviewGroup
                  preview={{
                    toolbarRender: (
                      _,
                      {
                        transform: { scale },
                        actions: {
                          onRotateLeft,
                          onRotateRight,
                          onZoomOut,
                          onZoomIn,
                        },
                      },
                    ) => (
                      <Space size={34} className="toolbar-wrapper">
                        <RotateLeftOutlined
                          style={{
                            fontSize: 20,
                          }}
                          onClick={onRotateLeft}
                        />
                        <RotateRightOutlined
                          style={{
                            fontSize: 20,
                          }}
                          onClick={onRotateRight}
                        />
                        <ZoomOutOutlined
                          // disabled={scale === 1}
                          onClick={onZoomOut}
                          style={{
                            fontSize: 20,
                          }}
                        />
                        <ZoomInOutlined
                          // disabled={scale === 50}
                          style={{
                            fontSize: 20,
                          }}
                          onClick={onZoomIn}
                        />
                      </Space>
                    ),
                  }}
                >
                  <ListView
                    data={catalogDetails?.otherImages}
                    renderItem={(item, i) => (
                      <Col
                        md={8}
                        key={i}
                        style={{
                          overflow: "hidden",
                        }}
                      >
                        <div
                          style={{
                            background: "#fff",
                            boxShadow: "0px 0px 9px #ccc",
                            borderRadius: "25px",
                            margin: "8px",
                          }}
                        >
                          <div
                            style={{
                              position: "relative",
                              overflow: "hidden",
                            }}
                          >
                            <div className="imageLayout">
                              <AntdImage
                                src={$public(item?.image?.path)}
                                alt={""}
                                style={{
                                  width: "100%",
                                  height: "25rem",
                                  objectFit: "cover",
                                  // borderRadius: "20px",
                                }}
                              />
                            </div>
                          </div>

                          {/* Date */}
                          <p className="month-detail">
                            {new Date(
                              item?.image?.createdAt,
                            ).toLocaleDateString("en-US", {
                              day: "2-digit",
                              month: "short",
                              year: "numeric",
                            })}
                          </p>

                          {/* Caption */}
                          <p
                            style={{
                              padding: "3px  6px 9px",
                              color: "#222222",
                              alignItems: "center",
                              fontSize: "16px",
                              fontWeight: "400",
                              margin: "0 4px",
                            }}
                          >
                            {item?.caption || ""}
                          </p>
                        </div>
                      </Col>
                    )}
                  />
                </AntdImage.PreviewGroup>
              </Row>
            </>
          )}
          {catalogDetails?.minerals?.length > 0 && (
            <div>
              <div className={`${styles.Upload}`}>
                <h6 className={`${styles.otherImages}`}>
                  {userName}
                  {userName !== "My" && "'s"} Minerals
                </h6>
              </div>

              <Row gutter={[16, 16]} style={{ marginBottom: "25px" }}>
                <ListView
                  data={catalogDetails?.minerals}
                  renderItem={(item, i) => (
                    <Col md={6} key={i}>
                      <MineralCard
                        item={item}
                        href={`/minerals/${encodeURIComponent(
                          item.slug || item._id
                        )}`}
                      />
                    </Col>
                  )}
                />
              </Row>
            </div>
          )}
        </div>
        <PopupModal
          className={`${styles.popup}`}
          visible={isSocialPopupVisible}
          onClose={() => setSocialPopupVisible(false)}
          title=""
          content={
            <SocialShare
              type="collection"
              customTitle="Check out this Collect Profile"
            />
          }
        />
      </Layout>
    </div>
  );
};

export default SingleCatalogView;
